import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {AlertState} from "@/lib/types/alerting.ts";

import {MonitoringServiceType} from "./checks";

export enum MonitoringGroupType {
  NORMAL = "NORMAL",
  DISABLED = "DISABLED",
  SINGLE_LOCATION_CHECKS = "SINGLE_LOCATION_CHECKS",
  DEDICATED = "DEDICATED",
  FREETOOLS = "FREETOOLS",
  TEST = "TEST",
  PRIVATE = "PRIVATE",
  TRANSACTION_EXT = "TRANSACTION_EXT",
}

export enum MonitoringGroupLevel {
  LEVEL_0 = "LEVEL_0",
  LEVEL_1 = "LEVEL_1",
  LEVEL_2 = "LEVEL_2",
  LEVEL_3 = "LEVEL_3",
}

export const MonitoringGroupLevelName = {
  [MonitoringGroupLevel.LEVEL_0]: "",
  [MonitoringGroupLevel.LEVEL_1]: "4",
  [MonitoringGroupLevel.LEVEL_2]: "Global",
  [MonitoringGroupLevel.LEVEL_3]: "Unlimited Global+",
};

export const MonitoringGroupLevelRank = {
  [MonitoringGroupLevel.LEVEL_0]: 0,
  [MonitoringGroupLevel.LEVEL_1]: 1,
  [MonitoringGroupLevel.LEVEL_2]: 2,
  [MonitoringGroupLevel.LEVEL_3]: 3,
};

export interface MonitoringGroup {
  id: number;
  location: string;
  code: string;
  country: string;
  is_private: boolean;
  level: MonitoringGroupLevel;
  type: MonitoringGroupType;
}

export interface MonitoringServer {
  id: number;
  group_id: number;
  name: string;
  address_ipv4: string;
  address_ipv6: string;
  address_ipv4_list: string[];
  address_ipv6_list: string[];
  hostname: string;
  is_nagios: boolean;
  is_active: boolean;
  compute_scale_factor: number;
  location: string;
  country: string;
  is_private: boolean;
  is_test: boolean;
  monitoring_level: MonitoringGroupLevel | null;
}

export interface DefaultLocationsNumberByLevel {
  level: MonitoringGroupLevel;
  number: number;
}

export interface DefaultLocationsNumberByType {
  type: MonitoringGroupType;
  number: number;
}

export interface DefaultLocationsNumber {
  byLevel: DefaultLocationsNumberByLevel[];
  byType: DefaultLocationsNumberByType[];
}

export interface MonitoringGroupsArchMapperEntry {
  monitoring_service_type: MonitoringServiceType;
  version: number;
  monitoring_group_ids: number[];
  has_nagios: boolean;
  has_belgrade: boolean;
}

export enum ServiceDiagnosisType {
  NAGIOS_STATUS = "NAGIOS_STATUS",
  TRACEROUTE = "TRACEROUTE",
}

export enum ServiceDiagnosticStatus {
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export interface ServiceDiagnosticData {
  monitoring_server_id: number;
  status: AlertState;
  current_attempt: number;
  max_attempts: number;
  last_check: string;
  next_check: string;
  last_state_change: string;
  plugin_output: string;
  is_private: boolean;
  error: boolean;
  created_at: string;
}

export interface ServiceDiagnostics {
  id: number;
  type: ServiceDiagnosisType;
  status: ServiceDiagnosticStatus;
  data: ServiceDiagnosticData[];
}

export enum PrivateLocationStatusCode {
  OK = "OK",
  ERROR = "ERROR",
}

export enum PrivateLocationGlobalStatusCode {
  ERROR = "ERROR",
  NOT_REPORTING = "NOT_REPORTING",
  OPERATIONAL = "OPERATIONAL",
  READY_TO_DEPLOY = "READY_TO_DEPLOY",
  WARNING = "WARNING",
}

export const PrivateLocationGlobalStatusCodeMap: Record<PrivateLocationGlobalStatusCode, PillStatus> = {
  [PrivateLocationGlobalStatusCode.ERROR]: PillStatus.ERROR,
  [PrivateLocationGlobalStatusCode.NOT_REPORTING]: PillStatus.NOT_REPORTING,
  [PrivateLocationGlobalStatusCode.OPERATIONAL]: PillStatus.OPERATIONAL,
  [PrivateLocationGlobalStatusCode.READY_TO_DEPLOY]: PillStatus.READY_TO_DEPLOY,
  [PrivateLocationGlobalStatusCode.WARNING]: PillStatus.WARNING,
};

export interface PrivateLocation {
  id: number;
  name: string;
  location: string;
  version: string;
  status: PrivateLocationStatus;
  api_token: string;
  num_services: number;
  created_at: string;
}

export interface PrivateLocationStatus {
  status?: PrivateLocationStatusCode;
  details?: PrivateLocationStatusDetails;
  sent_at?: string;
  description?: string;
  received_at?: string;
  image_major_version?: string;
  image_minor_version?: string;
}

export interface PrivateLocationStatusDetails {
  check_alert_queue?: PrivateLocationStatusDetailItem;
  check_load?: PrivateLocationStatusDetailItem;
  check_mem?: PrivateLocationStatusDetailItem;
  check_nag?: PrivateLocationStatusDetailItem;
  check_perfdata_log?: PrivateLocationStatusDetailItem;
  check_reconfig_log?: PrivateLocationStatusDetailItem;
  check_send_alert_errors?: PrivateLocationStatusDetailItem;
  check_stalled_check_detection_log?: PrivateLocationStatusDetailItem;
  check_total_procs?: PrivateLocationStatusDetailItem;
  check_txn_manager?: PrivateLocationStatusDetailItem;
  last_check_times?: PrivateLocationStatusDetailItem;
}

export interface PrivateLocationGlobalStatus {
  errors: string[];
  warnings: string[];
  globalStatusCode: PrivateLocationGlobalStatusCode;
}

export interface PrivateLocationWithGlobalStatus extends PrivateLocation, PrivateLocationGlobalStatus {}

export interface PrivateLocationStatusDetailItem {
  status: PrivateLocationStatusCode;
  description: string;
}

export enum Platform {
  UBUNTU = "UBUNTU",
  KUBERNETES = "KUBERNETES",
  PHOTON = "PHOTON",
  MANUAL = "MANUAL",
}

export const PlatformDisplay: Record<Platform, string> = {
  [Platform.UBUNTU]: "Ubuntu",
  [Platform.KUBERNETES]: "Kubernetes",
  [Platform.PHOTON]: "PhotonOS",
  [Platform.MANUAL]: "Universal Docker Deployment",
};

export enum PrivateLocationTab {
  STATUS = "STATUS",
  DEPLOY = "DEPLOY",
}

export enum StatusSeverity {
  WARNING,
  ERROR,
}

export const GLOBAL_STATUS_TITLE_MAP = {
  [PrivateLocationGlobalStatusCode.ERROR]: "One or more errors were detected with this private location server.",
  [PrivateLocationGlobalStatusCode.WARNING]: "One or more warnings were detected with this private location server.",
  [PrivateLocationGlobalStatusCode.NOT_REPORTING]: "This private location server is not communicating with Uptime.com.",
  [PrivateLocationGlobalStatusCode.OPERATIONAL]: "This private location server is operating correctly.",
  [PrivateLocationGlobalStatusCode.READY_TO_DEPLOY]: "Ready to deploy.",
};

export type PrivateLocationStatusMapKey = keyof typeof PRIVATE_LOCATIONS_STATUS_MAP;

export const PRIVATE_LOCATIONS_STATUS_MAP = {
  check_alert_queue: {
    description: `Several alerts were not sent successfully to Uptime.com. This may indicate
      intermittent network issues. Restarting the container after network issues are resolved
      should resolve this warning.`,
    severity: StatusSeverity.WARNING,
  },
  check_perfdata_queue: {
    description: `Several metrics were not sent successfully to Uptime.com. This may indicate
      intermittent network issues. Restarting the container after network issues are resolved
      should resolve this warning.`,
    severity: StatusSeverity.WARNING,
  },
  check_hda1: {
    description: "The system indicates the root filesystem may be low on disk space.",
    severity: StatusSeverity.WARNING,
  },
  check_mem: {
    description: `The system indicates available memory is low. This may negatively impact checks,
      particularly transaction and page speed checks.`,
    severity: StatusSeverity.WARNING,
  },
  check_nag: {
    description: "The check scheduler process is not currently running or failed to start.",
    severity: StatusSeverity.ERROR,
  },
  check_nagios: {
    description: `No checks have been reported as run in the last 15 minutes. Please check
      whether any checks have been assigned to this location, and/or restart this private
      location container. If this error remains, please ensure your container has the
      NET_BIND_SERVICE capability and that the apache service within is running.`,
    severity: StatusSeverity.ERROR,
  },
  check_nagios_all: {
    description: `One or more scheduled checks are not running in a timely fashion.
      Please restart this private location container. If this error persists, please
      ensure your container has the NET_BIND_SERVICE capability and that the apache
      service within is running.`,
    severity: StatusSeverity.ERROR,
  },
  check_perfdata_log: {
    description: "Response time data sync has failed to run or returned with error.",
    severity: StatusSeverity.ERROR,
  },
  check_reconfig_log: {
    description: `The private location has failed to check for and/or sync new configuration.
      Please check that your <code>UPTIME_API_TOKEN</code> setting is correct.`,
    severity: StatusSeverity.ERROR,
  },
  check_send_alert_errors: {
    description: "An error occurred attempting to send alert data to Uptime.com.",
    severity: StatusSeverity.ERROR,
  },
  check_stalled_check_detection_log: {
    description: `The process to synchronize check states with the Uptime.com system failed
      to run or returned with error.`,
    severity: StatusSeverity.WARNING,
  },
  check_total_procs: {
    description: `The system is reporting a very high number of processes running concurrently.
      This may indicate a large number of zombie processes or some other resource cleanup issue.`,
    severity: StatusSeverity.WARNING,
  },
  check_txn_manager: {
    description: `The Chrome transaction check system reported errors. If this does not
      resolve shortly, or after a restart, please contact support for further investigation.`,
    severity: StatusSeverity.ERROR,
  },
  check_vmstat: {
    description: `The system is reporting high CPU use, IO wait time or CPU steal. This may
      indicate insufficient resources allocated for the container. You can use the
      <code>vmstat</code> command for further investigation.`,
    severity: StatusSeverity.WARNING,
  },
};
